import TravelRequirementsBadge from "../components/travel/TravelRequirementsBadge";
import React, {useId, useState} from "react";
import {RoundTripFlightMatch} from "../models/RoundTripFlightMatch";
import {useTranslation} from "../i18n/LanguageProvider";
import {getImageUrl} from "../utils/GetImageUrl";
import RoundTripFlightInfoBlock from "../components/list/RoundTripFlightInfoBlock";
import ShareFlightButton from "../components/buttons/ShareFlightButton";
import BookmarkFlightButton from "../components/shortlist/BookmarkFlightButton";
import mapsIcon from "../assets/images/mobile/prototype/maps.png";
import arrow from "../assets/images/mobile/prototype/arrow-dark.svg";

interface Props {
    flightMatch: RoundTripFlightMatch;
    compact: boolean;
}

export default function MobileFlightResult({flightMatch, compact}: Props) {
    const {locale, t} = useTranslation();
    const flight = flightMatch.round_trip_flight;
    const outbound = flight.outbound_flight;
    const city = flight.destination_city || outbound.destination_airport;
    const [expanded, setExpanded] = useState(false);
    const detailsId = useId();
    const [imageSrc, setImageSrc] = useState(getImageUrl(outbound.destination_iata));
    const outboundLegs = flight.outbound_flights?.length ? flight.outbound_flights : [outbound];
    const inboundLegs = flight.inbound_flights?.length ? flight.inbound_flights : flight.inbound_flight ? [flight.inbound_flight] : [];
    const hasConnection = outboundLegs.length > 1 || inboundLegs.length > 1;
    const estimated = [...outboundLegs, ...inboundLegs].some(leg => leg.price_is_estimated);
    const price = `${estimated ? `${t("flight.fromPrice")} ` : ""}${flight.total_price.toFixed(0)}€`;
    const formatDate = (date: string) => new Date(date).toLocaleDateString(locale, {day: "2-digit", month: "2-digit"});
    const dates = `${formatDate(outboundLegs[0].departure_date)}${inboundLegs.length ? `–${formatDate(inboundLegs[0].departure_date)}` : ""}`;
    const toggle = () => setExpanded(value => !value);

    return (
        <article className={`mobile-flight-result ${compact ? "mobile-flight-compact" : "mobile-flight-photo"}`}>
            {compact ? (
                <div className="mobile-flight-row">
                    <button type="button" className="mobile-flight-destination min-w-0 text-left" onClick={toggle} aria-expanded={expanded} aria-controls={detailsId}>
                        <span className="mobile-flight-city">{city} <small>{outbound.destination_iata}</small></span>
                        <span className="mobile-flight-date">{dates}</span>
                    </button>
                    <BookmarkFlightButton flight={flight}/>
                    <ShareFlightButton flight={flight} compact className="shrink-0" />
                    <button type="button" className="flex shrink-0 items-center gap-2" onClick={toggle} aria-expanded={expanded} aria-controls={detailsId}>
                        <span className="mobile-flight-price">{price}</span>
                        <img src={arrow} alt="" className="mobile-row-arrow" />
                    </button>
                </div>
            ) : (
                <div className="mobile-flight-cover">
                    <img src={imageSrc} alt="" className="mobile-flight-image" loading="lazy"
                        onError={() => setImageSrc("/images/destinations/default.png")} />
                    <div className="mobile-flight-cover-shade" />
                    <div className="mobile-flight-cover-top">
                        <button type="button" className="mobile-flight-destination" onClick={toggle}
                            aria-label={`${t("mobile.details")}: ${city}`} aria-expanded={expanded} aria-controls={detailsId}>
                            <span className="mobile-flight-city">{city}</span>
                            <span className="mobile-flight-date">{dates}</span>
                        </button>
                        <span className="mobile-flight-price">{price}</span>
                    </div>
                    <div className="mobile-flight-cover-actions">
                        <BookmarkFlightButton flight={flight}/>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(city)}`}
                            target="_blank" rel="noopener noreferrer" className="mobile-map-link" aria-label={t("mobile.map", {city})}>
                            <img src={mapsIcon} alt="" />
                        </a>
                        {hasConnection ? (
                            <button type="button" className="mobile-provider-link" onClick={toggle}
                                aria-expanded={expanded} aria-controls={detailsId}>{t("flight.book")}</button>
                        ) : (
                            <a href={flight.booking_link} target="_blank" rel="noopener noreferrer" className="mobile-provider-link">{t("flight.book")}</a>
                        )}
                    </div>
                </div>
            )}
            {flightMatch.travel_requirements && <div className="px-4"><TravelRequirementsBadge requirements={flightMatch.travel_requirements} /></div>}
            {expanded && <div id={detailsId} className="mobile-flight-expanded flight-result-card">
                <RoundTripFlightInfoBlock roundTripFlight={flight} />
            </div>}
        </article>
    );
}
